import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './InboxSection.module.css';
import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';

// Pulls the raw markdown/text string out of a Field-shaped data object or string
const getRawContent = field => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.content || field.markdown || field.text || '';
};

// Extracts string content from title field safely
const getTitleContent = titleField => {
  if (!titleField) return '';
  if (typeof titleField === 'string') return titleField;
  return titleField.content || titleField.text || '';
};

// Default fallback rows matching approved Website Copy
const DEFAULT_INBOX_ROWS = [
  {
    blockId: 'inbox-row-1',
    title: { content: 'After the free trial' },
    text: {
      content:
        "Your child's starting level across the five skills, and the tutor's recommended focus",
    },
  },
  {
    blockId: 'inbox-row-2',
    title: { content: 'After every lesson' },
    text: {
      content: "A summary of what was taught and the tutor's note to you",
    },
  },
  {
    blockId: 'inbox-row-3',
    title: { content: 'On a level-up' },
    text: {
      content: 'Confirmation that a skill has advanced, and the certificate',
    },
  },
  {
    blockId: 'inbox-row-4',
    title: { content: 'Each term' },
    text: {
      content: 'A progress summary: what has changed, what comes next',
    },
  },
];

const DEFAULT_TITLE = 'What lands in your inbox';

const InboxSection = ({
  blocks = [],
  options = null,
  title = null,
  description = null,
  sectionId = 'inbox-section',
}) => {
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const [expanded, setExpanded] = useState(0); // first item open by default on mobile

  const hasTitle = hasDataInFields([title], fieldOptions) || Boolean(title);
  const hasDescription = hasDataInFields([description], fieldOptions);

  // Every block whose blockId starts with "inbox-row-" or "inbox-"
  let rowBlocks = blocks.filter(
    block => block?.blockId?.startsWith('inbox-row-') || block?.blockId?.startsWith('inbox-')
  );

  if (rowBlocks.length === 0) {
    rowBlocks = blocks.filter(block => block?.blockId?.startsWith('row-'));
  }
  if (rowBlocks.length === 0 && blocks.length > 0) {
    rowBlocks = blocks.filter(block => block?.title || block?.text);
  }
  if (rowBlocks.length === 0) {
    rowBlocks = DEFAULT_INBOX_ROWS;
  }

  const toggle = index => {
    setExpanded(prev => (prev === index ? -1 : index));
  };

  return (
    <section id={sectionId || 'inbox-section'} className={css.inboxSection}>
      {/* ---------- Centered Section Title ---------- */}
      <div className={css.titleWrapper}>
        {hasTitle ? (
          <Field data={title} className={css.title} options={fieldOptions} />
        ) : (
          <h2 className={css.title}>{DEFAULT_TITLE}</h2>
        )}

        {hasDescription ? (
          <div className={css.descriptionWrapper}>
            <Field data={description} className={css.description} options={fieldOptions} />
          </div>
        ) : null}
      </div>

      {/* ---------- Desktop Table ---------- */}
      <div className={css.tableWrapper}>
        <div className={css.tableHeader}>
          <span className={css.colWhen}>When</span>
          <span className={css.colReceive}>What you receive</span>
        </div>

        {rowBlocks.map((block, index) => {
          const titleText = getTitleContent(block?.title);
          const rawText = getRawContent(block?.text);
          const isAltRow = index % 2 === 1;

          return (
            <div
              key={block.blockId || `inbox-row-${index + 1}`}
              className={`${css.tableRow} ${isAltRow ? css.tableRowAlt : ''}`}
            >
              <div className={css.colWhen}>
                <span className={css.cellTextWhen}>
                  {titleText || (block?.title ? <Field data={block.title} options={fieldOptions} /> : null)}
                </span>
              </div>
              <div className={css.colReceive}>
                <div className={css.cellTextReceive}>
                  {rawText ? <p>{rawText}</p> : block?.text ? <Field data={block.text} options={fieldOptions} /> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ---------- Mobile Accordion ---------- */}
      <div className={css.accordionWrapper}>
        <div className={css.accordionContainer}>
          {rowBlocks.map((block, index) => {
            const titleText = getTitleContent(block?.title);
            const rawText = getRawContent(block?.text);
            const isOpen = expanded === index;

            return (
              <div
                key={block.blockId || `acc-inbox-${index + 1}`}
                className={`${css.accordionItem} ${isOpen ? css.accordionItemOpen : ''}`}
              >
                <button
                  type="button"
                  className={css.accordionHeader}
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                >
                  <span className={css.accordionLabel}>
                    <span className={css.accordionTitleText}>
                      {titleText || (block?.title ? <Field data={block.title} options={fieldOptions} /> : null)}
                    </span>
                  </span>
                  <svg
                    className={`${css.chevron} ${isOpen ? css.chevronOpen : ''}`}
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 8 10 12 14 8" />
                  </svg>
                </button>

                {isOpen ? (
                  <div className={css.accordionBody}>
                    <div className={css.accordionFieldGroup}>
                      <p className={css.accordionFieldLabel}>What you receive</p>
                      <div className={css.accordionFieldValue}>
                        {rawText ? <p>{rawText}</p> : block?.text ? <Field data={block.text} options={fieldOptions} /> : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

InboxSection.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  options: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  sectionId: PropTypes.string,
};

export default InboxSection;
